import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarOff } from 'lucide-react';
import ShiftCell from './ShiftCell';
import { LanguageBadge } from './LanguageBadge';
import { WhatsAppIcon } from './WhatsAppIcon';
import { formatCurrency } from '../utils/format';
import { isNeedsCover } from '../utils/scheduleMaps';

function formatDayHeader(date) {
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' });
  const day = date.getDate();
  return { weekday, day };
}

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function fmtMins(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

const MAX_NAME_DISPLAY = 25;
const truncateName = (n) =>
  n && n.length > MAX_NAME_DISPLAY ? n.slice(0, MAX_NAME_DISPLAY) + '…' : n;

function ScheduleGrid({
  employees,
  weekDays,
  shiftTemplates,
  shiftMap,
  leaveMap,
  savingCells,
  onCellClick,
  onSendClick,
  dailyTotals,
  perEmployeeMinutes,
  capWarningEmployeeIds,
  currency,
  cellsDisabled,
  cellsDisabledTitle,
}) {
  const { t } = useTranslation('scheduler');

  const templateMap = useMemo(
    () => new Map((shiftTemplates || []).map((tpl) => [tpl.id, tpl])),
    [shiftTemplates]
  );

  return (
    <div className="overflow-x-auto">
      <table className="min-w-[760px] w-full border-collapse text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider w-60 border-r border-gray-200">
              {t('grid.staffColumn')}
            </th>
            {weekDays.map((day) => {
              const { weekday, day: dayNum } = formatDayHeader(day);
              return (
                <th
                  key={day.toISOString()}
                  className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[100px]"
                >
                  <div>{weekday}</div>
                  <div className="text-base font-bold text-gray-700 normal-case tracking-normal leading-tight">
                    {dayNum}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-100 bg-white">
          {employees.map((emp) => {
            const name =
              emp.fullName ||
              emp.name ||
              `${emp.firstName ?? ''} ${emp.lastName ?? ''}`.trim();
            const initials = name
              .split(' ')
              .filter(Boolean)
              .slice(0, 2)
              .map((w) => w[0].toUpperCase())
              .join('');

            const isOverCap = capWarningEmployeeIds?.has(emp.id) ?? false;
            const scheduledMins = perEmployeeMinutes?.get(emp.id) ?? 0;
            const capHours = emp.weeklyHourCap ?? 48;

            return (
              <tr key={emp.id} className="hover:bg-gray-50 transition-colors">
                <td className="sticky left-0 z-10 bg-white hover:bg-gray-50 px-4 py-1 w-60 border-r border-gray-200">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-orange-100 text-orange-700 text-xs font-bold flex-shrink-0">
                      {initials || '?'}
                    </span>
                    <span
                      className="font-medium text-gray-800 truncate flex-1 min-w-0"
                      title={name}
                    >
                      {truncateName(name)}
                    </span>
                    <LanguageBadge code={emp.whatsappLanguage} />
                    {isOverCap && (
                      <div
                        className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0"
                        title={t('totals.capWarningTooltip', {
                          scheduledHours: Math.round(scheduledMins / 60),
                          capHours,
                        })}
                      />
                    )}
                    <button
                      type="button"
                      title={t('actions.sendToStaff')}
                      aria-label={t('actions.sendToStaff')}
                      onClick={() => onSendClick?.(emp)}
                      className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-green-500 hover:bg-green-600 flex-shrink-0 transition-colors focus:outline-none focus:ring-2 focus:ring-green-300 focus:ring-offset-1"
                    >
                      <WhatsAppIcon size={12} className="text-white" />
                    </button>
                  </div>
                </td>
                {weekDays.map((day) => {
                  const shiftDate = toYMD(day);
                  const cellKey = `${emp.id}-${shiftDate}`;
                  const shift = shiftMap ? shiftMap.get(cellKey) ?? null : null;
                  const isSaving = savingCells ? savingCells.has(cellKey) : false;
                  // Story 15 B1/B2 — overlay read, never a shift-row mutation. "Needs cover"
                  // flags a published shift that lost its person to a later-approved leave.
                  const onLeave = leaveMap ? leaveMap.has(cellKey) : false;
                  const needsCover = isNeedsCover(onLeave, shift);
                  return (
                    <td
                      key={day.toISOString()}
                      className="px-2 py-0.5 text-center align-middle"
                    >
                      {/* Story 15b Item 1 — badge is position:absolute so it never
                          participates in row flow: every cell's relative wrapper is exactly
                          ShiftCell's height, so all staff rows stay the same height and every
                          chip shares the same baseline, leave or no leave. */}
                      <div className="relative">
                        {onLeave && (
                          <div className="absolute -top-1.5 inset-x-0 z-10 flex items-center justify-center gap-1 pointer-events-none">
                            <span className="pointer-events-auto inline-flex items-center gap-0.5 rounded-full bg-white border border-amber-400 text-amber-700 text-[9px] font-bold px-1.5 py-[1px] shadow-sm whitespace-nowrap leading-none">
                              <CalendarOff size={9} />
                              {t('cell.onLeave')}
                            </span>
                            {needsCover && (
                              <span
                                className="pointer-events-auto w-1.5 h-1.5 rounded-full bg-red-500 ring-1 ring-white shadow-sm flex-shrink-0"
                                title={t('cell.needsCoverTooltip')}
                              />
                            )}
                          </div>
                        )}
                        <ShiftCell
                          shift={shift}
                          isSaving={isSaving}
                          templateMap={templateMap}
                          muted={needsCover}
                          disabled={cellsDisabled}
                          disabledTitle={cellsDisabledTitle}
                          onClick={(anchorRect) => onCellClick(emp.id, shiftDate, anchorRect)}
                        />
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}

          <tr className="border-t-2 border-gray-200 bg-gray-50">
            <td className="sticky left-0 z-10 bg-gray-50 px-4 py-3 border-r border-gray-200">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {t('grid.totalsRow')}
              </span>
            </td>
            {weekDays.map((day) => {
              const iso = toYMD(day);
              const dayData = dailyTotals?.get(iso);
              const mins = dayData?.minutes ?? 0;
              const cost = dayData?.costSmallestUnit ?? 0;

              if (mins === 0) {
                return (
                  <td key={day.toISOString()} className="px-2 py-3 text-center">
                    <span className="text-sm text-gray-300 font-medium">—</span>
                  </td>
                );
              }

              return (
                <td key={day.toISOString()} className="px-2 py-3 text-center">
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="text-xs font-semibold text-gray-700">{fmtMins(mins)}</span>
                    {cost > 0 && (
                      <span className="text-xs text-gray-500">
                        {formatCurrency(cost / 100)}
                      </span>
                    )}
                  </div>
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default memo(ScheduleGrid);